import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Alert,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  Google as GoogleIcon,
  Assessment as StatsIcon,
  ArrowBack as ArrowBackIcon,
  HowToReg as PendingIcon,
  Queue as QueueIcon,
  AttachMoney as PricingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import UserManagement from '../components/admin/UserManagement';
import PendingRegistrations from '../components/admin/PendingRegistrations';
import UsageStats from '../components/admin/UsageStats';
import StylistaQueue from '../components/admin/StylistaQueue';
import PricingInfo from '../components/admin/PricingInfo';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleBack = () => {
    navigate('/modes');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
              aria-label="back to modes"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0.5 }}>
                {t('admin.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="admin tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<PendingIcon />}
              label="Pending Registrations"
              id="admin-tab-0"
              aria-controls="admin-tabpanel-0"
            />
            <Tab
              icon={<PeopleIcon />}
              label={t('admin.tabs.users')}
              id="admin-tab-1"
              aria-controls="admin-tabpanel-1"
            />
            <Tab
              icon={<GoogleIcon />}
              label={t('admin.tabs.google')}
              id="admin-tab-2"
              aria-controls="admin-tabpanel-2"
              disabled
            />
            <Tab
              icon={<StatsIcon />}
              label={t('admin.tabs.stats')}
              id="admin-tab-3"
              aria-controls="admin-tabpanel-3"
            />
            <Tab
              icon={<QueueIcon />}
              label="Kolejka Stylista"
              id="admin-tab-4"
              aria-controls="admin-tabpanel-4"
            />
            <Tab
              icon={<PricingIcon />}
              label="Ceny"
              id="admin-tab-5"
              aria-controls="admin-tabpanel-5"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          <PendingRegistrations />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <UserManagement />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Alert severity="info">
            {t('admin.comingSoon')} - Google OAuth Whitelist Management
          </Alert>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <UsageStats />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <StylistaQueue />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <PricingInfo />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminPage;
